import { KeyValue, ValidationError } from "@skv/core";
import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";

interface MultiSelectProps {
  options: KeyValue[];
  selectedOptions: number[];
  onChange: (selectedOptions: number[]) => void;
  fieldName: string;
  isTouched?: boolean;
  errorMsg?: React.ReactNode;
  buttonLabel?: string;
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  selectedOptions,
  onChange,
  fieldName,
  isTouched,
  errorMsg,
  buttonLabel = "All",
}) => {
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleOptionChange = (value: number) => {
    const updatedSelection = [...selectedOptions];
    const index = updatedSelection.indexOf(value);
    if (index > -1) {
      updatedSelection.splice(index, 1);
    } else {
      updatedSelection.push(value);
    }
    onChange(updatedSelection);
  };

  const toggleDropdown = () => {
    setDropdownOpen(!isDropdownOpen);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setDropdownOpen(false);
    }
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, []);

  const getButtonLabel = () => {
    const numSelected = selectedOptions.length;

    if (numSelected === 1) {
      const selectedOption = options.find(
        (option) => option.id === selectedOptions[0]
      );
      return selectedOption ? selectedOption.name : `Select ${fieldName}`;
    }

    return numSelected > 1
      ? `${numSelected} selected ${fieldName}`
      : buttonLabel;
  };

  return (
    <>
      <div
        ref={dropdownRef}
        className={`dropdown${isDropdownOpen ? " show" : ""}`}
      >
        <button
          className={classNames(
            "btn btn-block dropdown-toggle d-flex align-items-center justify-content-between font-weight-normal",
            { "form-control is-invalid": isTouched && !!errorMsg }
          )}
          css={`
            border-color: #5a6881;
          `}
          type="button"
          onClick={toggleDropdown}
        >
          {getButtonLabel()}
        </button>

        {typeof window !== "undefined" && (
          <div
            className={classNames(
              "w-100 dropdown-menu custom-control custom-checkbox rounded-0 pl-0 mb-1",
              isDropdownOpen ? "d-block" : "d-none"
            )}
            css={`
              max-height: 200px;
              overflow-y: auto;
            `}
          >
            {options.map((option) => (
              <div key={option.id} className="dropdown-item text-dark pl-5">
                <input
                  id={`${fieldName}-${option.id}`}
                  name={fieldName}
                  className="custom-control-input mr-2"
                  type="checkbox"
                  value={option.id}
                  checked={selectedOptions.includes(option.id)}
                  onChange={(e) => handleOptionChange(Number(e.target.value))}
                />
                <label
                  htmlFor={`${fieldName}-${option.id}`}
                  className="custom-control-label text-dark"
                >
                  {option.name}
                </label>
              </div>
            ))}
          </div>
        )}
      </div>

      <ValidationError
        errorId={fieldName.split(" ").join("-") + "-error-message"}
        className={classNames({ "d-block my-1": isTouched && !!errorMsg })}
        message={isTouched && !!errorMsg ? errorMsg : null}
      />
    </>
  );
};
